import { useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, FileText, Paperclip, Trash2 } from 'lucide-react';
import type { AnnouncementAttachment, AttachmentInput, UploadedDocument } from '../api/announcements';
import {
  ATTACHMENT_LABEL_MAX,
  ATTACHMENT_URL_MAX,
  documentKindLabel,
  formatByteSize,
  labelFromFileName,
  linkAddressProblem,
  moveItem,
} from '../lib/attachments';
import { Button } from './Button';
import { TextField } from './Field';

/**
 * The attachments half of a form that hands things out — an announcement's (TROOP-255) and an Info
 * page section's (TROOP-256). Lifted out of the announcement form when the second one arrived, so
 * the two cannot drift into asking for a link two different ways: one list, one row, one wire shape.
 *
 * A **document** uploads the moment it is picked and rides the save as a file id; a **link** is a
 * label and an address in boxes of their own; a **kept** row is one already saved, which a save
 * names by id and can move or remove but not relabel — a changed label is a new attachment.
 */

/** A document on the form. `storedFileId` is null while the upload is in flight. */
export interface DraftDocument {
  key: number;
  kind: 'Document';
  fileName: string;
  label: string;
  storedFileId: string | null;
  contentType: string;
  byteSize: number;
}

/** A link on the form: a label and an address, typed into their own boxes. */
export interface DraftLink {
  key: number;
  kind: 'Link';
  label: string;
  url: string;
  /** The address box has been left once, so its pre-check may speak. Not before: mid-word is not wrong. */
  touched: boolean;
}

/** One already saved, kept by id. What it says about itself is read off the saved row. */
export interface DraftKept {
  key: number;
  kind: 'Kept';
  id: string;
  label: string;
  /** "PDF · 12 KB", or "goes to council.example.org". */
  caption: string;
  isLink: boolean;
}

export type DraftAttachment = DraftDocument | DraftLink | DraftKept;

/** Saved attachments as kept rows, for a form that edits something already published. */
export function keptDrafts(saved: AnnouncementAttachment[], firstKey: number): DraftKept[] {
  return saved.map((a, i) => ({
    key: firstKey + i,
    kind: 'Kept',
    id: a.id,
    label: a.label,
    isLink: a.kind === 'Link',
    caption:
      a.kind === 'Link'
        ? `goes to ${a.linkHost ?? 'an address'}`
        : a.file
          ? `${documentKindLabel(a.file.contentType)} · ${formatByteSize(a.file.byteSize)}`
          : 'Document',
  }));
}

/**
 * The rows and what can be done to them. `upload` is the form's own document door — each form's
 * bytes land under its own prefix, which is the server's check that a file came through it.
 */
export function useAttachmentDrafts(
  upload: (file: File) => Promise<UploadedDocument>,
  initial: DraftAttachment[] = [],
) {
  const [rows, setRows] = useState<DraftAttachment[]>(initial);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const nextKey = useRef(initial.reduce((max, row) => Math.max(max, row.key + 1), 0));

  function update(key: number, change: Partial<DraftDocument> | Partial<DraftLink>) {
    setRows((current) =>
      current.map((row) => (row.key === key ? ({ ...row, ...change } as DraftAttachment) : row)),
    );
  }

  function remove(key: number) {
    setRows((current) => current.filter((row) => row.key !== key));
  }

  function move(index: number, delta: -1 | 1) {
    setRows((current) => moveItem(current, index, delta));
  }

  function addLink() {
    const key = nextKey.current++;
    setRows((current) => [...current, { key, kind: 'Link', label: '', url: '', touched: false }]);
  }

  /** Uploads the moment a file is picked, so saving only has to name what is already there. */
  async function addDocument(file: File) {
    const key = nextKey.current++;
    setUploadError(null);
    setRows((current) => [
      ...current,
      {
        key,
        kind: 'Document',
        fileName: file.name,
        label: labelFromFileName(file.name),
        storedFileId: null,
        contentType: file.type,
        byteSize: file.size,
      },
    ]);

    try {
      const uploaded = await upload(file);
      update(key, {
        storedFileId: uploaded.storedFileId,
        contentType: uploaded.contentType,
        byteSize: uploaded.byteSize,
      });
    } catch (e) {
      // The row goes, and the server's own sentence says why.
      remove(key);
      setUploadError(`${file.name}: ${e instanceof Error ? e.message : 'That file could not be attached.'}`);
    }
  }

  /**
   * What a save sends, composed from the rows on screen in the order they are drawn — never from a
   * second list kept beside them. A document still uploading is left out; the form's submit
   * refuses while one is (see `incomplete`).
   */
  function toInput(): (AttachmentInput | { id: string })[] {
    return rows.flatMap((row): (AttachmentInput | { id: string })[] =>
      row.kind === 'Kept'
        ? [{ id: row.id }]
        : row.kind === 'Link'
          ? [{ kind: 'Link', label: row.label.trim(), url: row.url.trim() }]
          : row.storedFileId
            ? [{ kind: 'Document', storedFileId: row.storedFileId, label: row.label.trim() }]
            : [],
    );
  }

  const uploading = rows.some((row) => row.kind === 'Document' && !row.storedFileId);

  /** Why a save would be refused before it is sent, or undefined. For `Button`'s `disabledReason`. */
  const incomplete = uploading
    ? 'Wait for the file to finish uploading.'
    : rows.some((row) => row.kind !== 'Kept' && !row.label.trim())
      ? 'Give each attachment a label.'
      : rows.some((row) => row.kind === 'Link' && !row.url.trim())
        ? 'Paste an address for each link.'
        : undefined;

  return { rows, uploadError, uploading, incomplete, update, remove, move, addLink, addDocument, toInput };
}

export type AttachmentDrafts = ReturnType<typeof useAttachmentDrafts>;

/**
 * The attachments section of a form: a heading, what the caller has to say about it, the rows, and
 * the two add buttons — which stop at the cap rather than greying out, with the reason in their
 * place.
 */
export function AttachmentDraftsEditor({
  drafts,
  limit,
  atLimitMessage,
  error,
  children,
}: {
  drafts: AttachmentDrafts;
  limit: number;
  /** Said in place of the add buttons once the list is full. */
  atLimitMessage: string;
  /** The server's sentence about the attachments, from a refused save. */
  error?: string;
  /** What this form has to say about its attachments, under the heading. */
  children?: ReactNode;
}) {
  const heading = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const { rows } = drafts;

  return (
    <section aria-labelledby={heading} className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
      <div className="flex flex-col" style={{ gap: 'var(--space-1)' }}>
        <h2
          id={heading}
          className="t-label inline-flex items-center gap-2"
          style={{ color: 'var(--color-text-muted)', margin: 0 }}
        >
          <Paperclip size={14} aria-hidden="true" />
          Attachments
          <span style={{ textTransform: 'none', letterSpacing: 0 }}> (optional)</span>
        </h2>
        {children}
      </div>

      {rows.length > 0 && (
        <ol className="flex flex-col" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 'var(--space-3)' }}>
          {rows.map((row, index) => (
            <DraftRow
              key={row.key}
              row={row}
              position={index + 1}
              count={rows.length}
              onChange={(change) => drafts.update(row.key, change)}
              onRemove={() => drafts.remove(row.key)}
              onMoveUp={index > 0 ? () => drafts.move(index, -1) : undefined}
              onMoveDown={index < rows.length - 1 ? () => drafts.move(index, 1) : undefined}
            />
          ))}
        </ol>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            void drafts.addDocument(file);
          }
          e.target.value = '';
        }}
      />

      {rows.length >= limit ? (
        <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          {atLimitMessage}
        </p>
      ) : (
        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" onClick={() => fileInput.current?.click()}>
            <FileText size={16} aria-hidden="true" />
            Add a document
          </Button>
          <Button variant="secondary" onClick={drafts.addLink}>
            <ExternalLink size={16} aria-hidden="true" />
            Add a link
          </Button>
        </div>
      )}

      {drafts.uploadError && (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
          {drafts.uploadError}
        </p>
      )}

      {error && (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
          {error}
        </p>
      )}
    </section>
  );
}

/**
 * One attachment being prepared. SPEC-GAP: §6 has no list editor; built from the card surface,
 * `--color-border-subtle`, `--radius-md` and the existing `Field` boxes, with the move and remove
 * controls the program plan's rows already use. The first row has no *move up* and the last no
 * *move down* — absent rather than disabled, since there is nothing to explain.
 */
function DraftRow({
  row,
  position,
  count,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  row: DraftAttachment;
  position: number;
  count: number;
  onChange: (change: Partial<DraftDocument> | Partial<DraftLink>) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const name = row.label.trim() || (row.kind === 'Document' ? row.fileName : 'link');
  const addressProblem =
    row.kind === 'Link' && row.touched && row.url.trim() ? linkAddressProblem(row.url) : null;
  const isLink = row.kind === 'Link' || (row.kind === 'Kept' && row.isLink);

  return (
    <li
      aria-label={`Attachment ${position} of ${count}`}
      className="flex flex-col"
      style={{
        gap: 'var(--space-3)',
        background: 'var(--color-surface-raised)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
      }}
    >
      <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
        <span
          className="t-caption inline-flex items-center gap-2"
          style={{ color: 'var(--color-text-secondary)', flex: '1 1 8rem', minWidth: '6rem' }}
        >
          {isLink ? (
            <ExternalLink size={16} aria-hidden="true" className="shrink-0" />
          ) : (
            <FileText size={16} aria-hidden="true" className="shrink-0" />
          )}
          {row.kind === 'Kept' ? (
            <span style={{ overflowWrap: 'anywhere' }}>
              <strong style={{ color: 'var(--color-text-primary)' }}>{row.label}</strong> &middot; {row.caption}
            </span>
          ) : row.kind === 'Document' ? (
            row.storedFileId ? (
              <span>
                {documentKindLabel(row.contentType)} &middot; {formatByteSize(row.byteSize)}
              </span>
            ) : (
              <span role="status" style={{ overflowWrap: 'anywhere' }}>
                Uploading {row.fileName}&hellip;
              </span>
            )
          ) : (
            <span>Link</span>
          )}
        </span>

        {onMoveUp && <RowAction label={`Move ${name} up`} onClick={onMoveUp} icon={ChevronUp} />}
        {onMoveDown && <RowAction label={`Move ${name} down`} onClick={onMoveDown} icon={ChevronDown} />}
        <RowAction label={`Remove ${name}`} onClick={onRemove} icon={Trash2} />
      </div>

      {row.kind !== 'Kept' && (
        <TextField
          label="Label"
          required
          value={row.label}
          maxLength={ATTACHMENT_LABEL_MAX}
          onChange={(e) => onChange({ label: e.currentTarget.value })}
          hint={
            row.kind === 'Document'
              ? 'What families see. The download is named from it too.'
              : 'What families see and tap.'
          }
        />
      )}

      {row.kind === 'Link' && (
        <TextField
          label="Address"
          required
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          placeholder="https://"
          value={row.url}
          maxLength={ATTACHMENT_URL_MAX}
          onChange={(e) => onChange({ url: e.currentTarget.value })}
          onBlur={() => onChange({ touched: true })}
          error={addressProblem ?? undefined}
          hint="Families see the address this actually goes to, beside the label. Paste the whole thing."
        />
      )}
    </li>
  );
}

export function RowAction({
  label,
  onClick,
  icon: Icon,
}: {
  label: string;
  onClick: () => void;
  icon: typeof Trash2;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="inline-flex items-center justify-center rounded-md shrink-0"
      style={{
        minHeight: 'var(--tap-min)',
        minWidth: 'var(--tap-min)',
        background: 'none',
        border: 'none',
        color: 'var(--color-text-muted)',
        cursor: 'pointer',
      }}
    >
      <Icon size={16} aria-hidden="true" />
    </button>
  );
}
